import React from 'react';
import memoizeLast from '@sift/resift/memoizeLast';
import searchList from '@sift/skipper/helpers/searchList';
import { SimpleSet } from 'helpers/setOperations';

// Styles
import withStyles from '@material-ui/core/styles/withStyles';
import { defineStyles, Classes } from '@sift/skipper/styles/defineStyles';

// Components
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { ButtonProps } from '@material-ui/core/Button';
import AttributeSelectorBase, {
  AttributeSelectorBaseProps,
} from 'components/attributeSelector/Base';
import NoResultsView from '@sift/skipper/components/views/NoResults';

const styles = defineStyles(theme => ({
  noResultsView: {
    flex: '1 1 auto',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  root: {},
  attributeSelector: {},
  attribute: {},
  header: {},
  title: {},
  search: {},
  attributes: {},
  attributeRow: {},
  actions: {},
  clearAll: {},
  cancel: {},
  save: {},
  afterListView: {},
}));

interface Props<T> {
  className?: string;
  classes: Classes<typeof styles>;
  attributes: T[];
  getAttributeKey: (t: T) => string;
  /**
   * maps attributes to display names/strings. The local search algorithm will
   * also use this function to search against all the attributes
   */
  getAttributeDisplayName: (t: T) => string;
  /**
   * callback invoked when the user saves their selected attributes.
   *
   * this is called with the state of `selectedAttributes` set (where the set
   * is implemented with an object)
   */
  onSaveSelectedAttributes: (set: SimpleSet) => void;
  searchPlaceholder?: string;
  label: string;
  title: string;
  selectedAttributes: SimpleSet;
  renderAttribute?: (t: T) => React.ReactNode;
  LabelButtonProps?: ButtonProps;
  saveButtonLabel?: string;
}

interface State {
  open: boolean;
  search: string;
  selectedAttributes: SimpleSet;
}

class AttributeSelectorLocalSearch<T> extends React.PureComponent<Props<T>, State> {
  state: State = {
    open: false,
    search: '',
    selectedAttributes: {},
  };

  componentDidUpdate(_: Props<T>, previousState: State) {
    const closedBefore = !previousState.open;
    const openNow = this.state.open;

    if (closedBefore && openNow) {
      // copies the selected attributes from the props into state
      // eslint-disable-next-line
      this.setState({ selectedAttributes: this.props.selectedAttributes });
    }
  }

  get isAllSelected() {
    return this._getOrCalculateIsAllSelected(this.state.selectedAttributes, this.props.attributes);
  }

  _getOrCalculateIsAllSelected = memoizeLast((selectedAttributes: SimpleSet, attributes: T[]) => {
    const { getAttributeKey } = this.props;
    for (const key of attributes.map(getAttributeKey)) {
      if (!selectedAttributes[key]) return false;
    }
    return true;
  });

  get isPartiallySelected() {
    return this._getOrCalculateIsPartiallySelected(
      this.state.selectedAttributes,
      this.props.attributes,
    );
  }
  _getOrCalculateIsPartiallySelected = memoizeLast(
    (selectedAttributes: SimpleSet, attributes: T[]) => {
      if (this.isAllSelected) return false;

      const { getAttributeKey } = this.props;
      for (const key of attributes.map(getAttributeKey)) {
        if (selectedAttributes[key]) return true;
      }
      return false;
    },
  );

  get filteredAttributes() {
    return this._getOrCalculateFilteredAttributes(this.props.attributes, this.state.search);
  }

  _getOrCalculateFilteredAttributes = memoizeLast((attributes: T[], search: string) => {
    const { getAttributeDisplayName } = this.props;

    return searchList(attributes, getAttributeDisplayName, search);
  });

  onSelectAll = () => {
    if (this.isAllSelected) {
      this.onClearAll();
      return;
    }

    const { attributes, getAttributeKey } = this.props;

    const selectedAttributes = attributes
      .map(getAttributeKey)
      .reduce((newSelectedAttributes, key) => {
        newSelectedAttributes[key] = true;
        return newSelectedAttributes;
      }, {} as SimpleSet);

    this.setState(previousState => ({
      ...previousState,
      selectedAttributes,
    }));
  };

  onClearAll = () => {
    this.setState({ selectedAttributes: {} });
  };

  onOpen = () => {
    this.setState({ open: true });
  };

  onClose = () => {
    this.setState({ open: false });
  };

  onSearch = (search: string) => {
    this.setState({ search });
  };

  onChangeSelectedAttributes = (selectedAttributes: SimpleSet) => {
    this.setState({ selectedAttributes });
  };

  onSave = () => {
    this.props.onSaveSelectedAttributes(this.state.selectedAttributes);
  };

  renderAttribute = (attribute: T) => {
    if (this.props.renderAttribute) {
      return this.props.renderAttribute(attribute);
    }

    return this.props.getAttributeDisplayName(attribute);
  };

  renderHeader = (props: AttributeSelectorBaseProps<T>) => {
    const { classes, title } = props;

    return (
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>
        <FormControlLabel
          control={
            <Checkbox
              checked={this.isAllSelected || this.isPartiallySelected}
              indeterminate={this.isPartiallySelected}
              onChange={this.onSelectAll}
            />
          }
          label="Select All"
        />
      </div>
    );
  };

  listWrapper = (listElement: React.ReactNode) =>
    this.filteredAttributes.length <= 0 ? (
      <NoResultsView className={this.props.classes.noResultsView} />
    ) : (
      listElement
    );

  render() {
    const {
      getAttributeKey,
      className,
      searchPlaceholder,
      label,
      title,
      LabelButtonProps,
      classes,
      saveButtonLabel,
    } = this.props;

    const { noResultsView, ...restOfClasses } = classes;

    return (
      <AttributeSelectorBase<T>
        className={className}
        classes={restOfClasses}
        open={this.state.open}
        selectedAttributes={this.state.selectedAttributes}
        attributes={this.filteredAttributes}
        renderAttribute={this.renderAttribute}
        renderHeader={this.renderHeader}
        onOpen={this.onOpen}
        onClose={this.onClose}
        onSearch={this.onSearch}
        onSave={this.onSave}
        onClearAll={this.onClearAll}
        onChangeSelectedAttributes={this.onChangeSelectedAttributes}
        listWrapper={this.listWrapper}
        getAttributeKey={getAttributeKey}
        searchPlaceholder={searchPlaceholder}
        label={label}
        title={title}
        LabelButtonProps={LabelButtonProps}
        saveButtonLabel={saveButtonLabel}
      />
    );
  }
}

export default withStyles(styles)(AttributeSelectorLocalSearch);
